import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getRecetasModuleContext } from '../../../utils/recetasModuleContext';
import { useRecetaClassification } from '../../../contexts/RecetaClassificationContext';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import recetaPlatoService from '../../../services/recetaPlatoService';
import produccionLineaService from '../../../services/produccionLineaService';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import RecetasImportPreviewModal from '../../../components/ProduccionConcesionaria/RecetasImportPreviewModal';
import { RECETA_OUTPUT_PRODUCT_TYPES, resolveFabricarSubrecetaDesdePt } from '../../../data/mockRecetasClassification';
import {
  downloadRecetasImportTemplate,
  analyzeRecetasImport,
  executeRecetasImport,
  summarizeRecetasImportExec,
} from '../../../utils/recetasExcelImport';
import {
  detectRecetasExcelImportMode,
  analyzeRecetasPepUsoBulkUpdate,
  executeRecetasPepUsoBulkUpdate,
  summarizeRecetasPepUsoBulkUpdate,
  RECETAS_IMPORT_MODE,
} from '../../../utils/recetasExcelPepUsoUpdate';
import { downloadRecetasProductsExcel } from '../../../utils/recetasExcelExport';
import { getUnits } from '../../../services/unitsService';

function outputTypeBadge(row) {
  const type = row?.output_product_type || 'producto_terminado';
  const meta = RECETA_OUTPUT_PRODUCT_TYPES.find((o) => o.value === type);
  const label = meta?.label || 'Producto terminado';
  const isProceso = type === 'producto_en_proceso';
  const isSalidaInsumo = type === 'salida_insumo';
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
        isSalidaInsumo
          ? 'bg-emerald-100 text-emerald-900'
          : isProceso
            ? 'bg-amber-100 text-amber-900'
            : 'bg-sky-100 text-sky-900'
      }`}
      title={
        isSalidaInsumo
          ? 'Co-salida del lote; sigue siendo insumo del catálogo'
          : isProceso
            ? 'Insumo intermedio para otras elaboraciones'
            : 'Producto final para venta'
      }
    >
      {label}
    </span>
  );
}

function pepUsoEnPtBadge(row, globalDefault) {
  if (row?.output_product_type !== 'producto_en_proceso') return '—';
  const usarSubreceta = resolveFabricarSubrecetaDesdePt(row.fabricar_subreceta_desde_pt, globalDefault);
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
        usarSubreceta ? 'bg-violet-100 text-violet-900' : 'bg-orange-100 text-orange-900'
      }`}
      title={
        usarSubreceta
          ? 'Se fabrica automáticamente al sacar guía de producto terminado'
          : 'Se produce con guía de producto en proceso y se consume del almacén'
      }
    >
      {usarSubreceta ? 'Subreceta automática' : 'Guía PEP'}
    </span>
  );
}

const ESTADO_FILTRO = {
  TODOS: '',
  ACTIVO: 'activo',
  INACTIVO: 'inactivo',
};

/**
 * Listado de recetas desde API (/recetas/platos).
 */
const RecetasListado = () => {
  const location = useLocation();
  const { moduleBase, moduleName } = getRecetasModuleContext(location.pathname);
  const { produccionEnabled, produccionLineasEnabled, produccionFabricarPepDesdePtEnabled } = useModuleSettings();
  const includeOutputConfig = produccionEnabled && moduleBase === '/produccion';
  const showLineaFilter = produccionLineasEnabled && moduleBase === '/produccion';
  const listRecetasBase = `${moduleBase}/clasificacion-recetas/recetas`;
  const { hierarchy, insumoProducts, loading: loadingClasificacion } = useRecetaClassification();
  const navigate = useNavigate();

  const [nombreBusqueda, setNombreBusqueda] = useState('');
  const [debouncedNombre, setDebouncedNombre] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [subcategoriaId, setSubcategoriaId] = useState('');
  const [familiaId, setFamiliaId] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState(ESTADO_FILTRO.TODOS);
  const [lineaFiltro, setLineaFiltro] = useState('');
  const [lineas, setLineas] = useState([]);

  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [importPreview, setImportPreview] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const recetasImportInputRef = useRef(null);

  const categoriaSeleccionada = useMemo(
    () => hierarchy.find((c) => String(c.id) === String(categoriaId)),
    [hierarchy, categoriaId],
  );

  const subcategoriasFiltradas = useMemo(
    () => categoriaSeleccionada?.subcategories ?? [],
    [categoriaSeleccionada],
  );

  const subcategoriaSeleccionada = useMemo(
    () => subcategoriasFiltradas.find((s) => String(s.id) === String(subcategoriaId)),
    [subcategoriasFiltradas, subcategoriaId],
  );

  const familiasFiltradas = useMemo(
    () => subcategoriaSeleccionada?.families ?? [],
    [subcategoriaSeleccionada],
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedNombre(nombreBusqueda), 400);
    return () => clearTimeout(t);
  }, [nombreBusqueda]);

  useEffect(() => {
    if (!showLineaFilter) return;
    produccionLineaService.list({ active_only: true }).then((res) => {
      if (res.success) setLineas(res.data || []);
    }).catch(() => {});
  }, [showLineaFilter]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setListError(null);
    try {
      const params = {};
      if (debouncedNombre.trim()) params.search = debouncedNombre.trim();
      if (categoriaId) params.receta_category_id = categoriaId;
      if (subcategoriaId) params.receta_subcategory_id = subcategoriaId;
      if (familiaId) params.receta_family_id = familiaId;
      if (estadoFiltro === ESTADO_FILTRO.ACTIVO) params.is_active = true;
      if (estadoFiltro === ESTADO_FILTRO.INACTIVO) params.is_active = false;
      if (showLineaFilter && lineaFiltro) params.produccion_linea_id = lineaFiltro;

      const res = await recetaPlatoService.list(params);
      if (res.success && Array.isArray(res.data)) {
        setRows(res.data);
      } else {
        setRows([]);
        setListError(res.message || 'No se pudo cargar el listado');
      }
    } catch (e) {
      setRows([]);
      setListError(e.response?.data?.message || e.message || 'Error al cargar recetas');
    } finally {
      setLoadingList(false);
    }
  }, [debouncedNombre, categoriaId, subcategoriaId, familiaId, estadoFiltro, showLineaFilter, lineaFiltro]);

  useEffect(() => {
    if (loadingClasificacion) return;
    loadList();
  }, [loadingClasificacion, loadList]);

  const hayFiltrosActivos =
    Boolean(nombreBusqueda.trim()) ||
    Boolean(categoriaId) ||
    Boolean(subcategoriaId) ||
    Boolean(familiaId) ||
    estadoFiltro !== ESTADO_FILTRO.TODOS ||
    Boolean(lineaFiltro);

  const limpiarFiltros = () => {
    setNombreBusqueda('');
    setCategoriaId('');
    setSubcategoriaId('');
    setFamiliaId('');
    setEstadoFiltro(ESTADO_FILTRO.TODOS);
    setLineaFiltro('');
  };

  const onCategoriaChange = (e) => {
    const v = e.target.value;
    setCategoriaId(v);
    setSubcategoriaId('');
    setFamiliaId('');
  };

  const onSubcategoriaChange = (e) => {
    const v = e.target.value;
    setSubcategoriaId(v);
    setFamiliaId('');
  };

  const openDeleteModal = (row) => {
    if (!row?.can_delete) return;
    setRecipeToDelete(row);
    setDeleteModalOpen(true);
  };

  const onPickRecetasImportFile = () => {
    if (importing) return;
    const el = recetasImportInputRef.current;
    if (el) el.value = '';
    el?.click();
  };

  const onRecetasImportFileChange = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const importMode = includeOutputConfig
        ? await detectRecetasExcelImportMode(file)
        : RECETAS_IMPORT_MODE.CREATE;

      let analysis;
      if (importMode === RECETAS_IMPORT_MODE.PEP_USO_BULK_UPDATE) {
        const listRes = await recetaPlatoService.list({});
        analysis = await analyzeRecetasPepUsoBulkUpdate(file, {
          existingRecipes: listRes?.data || [],
          globalDefault: produccionFabricarPepDesdePtEnabled,
        });
      } else {
        analysis = await analyzeRecetasImport(file, {
          hierarchy,
          insumoProducts,
          includeOutputConfig,
          globalFabricarSubrecetaDefault: produccionFabricarPepDesdePtEnabled,
        });
      }

      if (!analysis.success) {
        setToast({
          isOpen: true,
          message: analysis.fatalMessage || 'No se pudo leer el archivo',
          type: 'error',
        });
        return;
      }
      setImportPreview(analysis);
      setImportModalOpen(true);
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'Error al leer el Excel',
        type: 'error',
      });
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setImportModalOpen(false);
    setImportPreview(null);
  };

  const handleExportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = {};
      if (debouncedNombre.trim()) params.search = debouncedNombre.trim();
      if (categoriaId) params.receta_category_id = categoriaId;
      if (subcategoriaId) params.receta_subcategory_id = subcategoriaId;
      if (familiaId) params.receta_family_id = familiaId;
      if (estadoFiltro === ESTADO_FILTRO.ACTIVO) params.is_active = true;
      if (estadoFiltro === ESTADO_FILTRO.INACTIVO) params.is_active = false;

      const [exportRes, unitsRes] = await Promise.all([
        recetaPlatoService.exportDetalle(params),
        getUnits({ is_active: true }),
      ]);

      if (!exportRes.success || !Array.isArray(exportRes.data)) {
        setToast({
          isOpen: true,
          message: exportRes.message || 'No se pudo generar el reporte',
          type: 'error',
        });
        return;
      }

      if (exportRes.data.length === 0) {
        setToast({
          isOpen: true,
          message: 'No hay recetas que coincidan con los filtros para exportar',
          type: 'error',
        });
        return;
      }

      const units = unitsRes?.success && Array.isArray(unitsRes.data) ? unitsRes.data : [];
      await downloadRecetasProductsExcel({
        recipes: exportRes.data,
        includeOutputConfig,
        units,
        globalFabricarSubrecetaDefault: produccionFabricarPepDesdePtEnabled,
      });

      setToast({
        isOpen: true,
        message: `Reporte generado (${exportRes.data.length} receta${exportRes.data.length === 1 ? '' : 's'})`,
        type: 'success',
      });
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || e.message || 'Error al exportar Excel',
        type: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  const confirmRecetasImport = async () => {
    if (!importPreview?.success) return;

    if (importPreview.mode === RECETAS_IMPORT_MODE.PEP_USO_BULK_UPDATE) {
      if (!importPreview.validUpdates?.length) return;
      setImporting(true);
      try {
        const exec = await executeRecetasPepUsoBulkUpdate(importPreview.validUpdates, recetaPlatoService);
        const msg = summarizeRecetasPepUsoBulkUpdate(exec);
        setToast({
          isOpen: true,
          message: msg,
          type: exec.errors?.length ? 'error' : 'success',
        });
        closeImportModal();
        await loadList();
      } catch (err) {
        setToast({
          isOpen: true,
          message: err?.message || 'Error al actualizar',
          type: 'error',
        });
      } finally {
        setImporting(false);
      }
      return;
    }

    if (!importPreview.validRecipes?.length) return;
    setImporting(true);
    try {
      const exec = await executeRecetasImport(importPreview.validRecipes, recetaPlatoService);
      const msg = summarizeRecetasImportExec(exec);
      setToast({
        isOpen: true,
        message: msg,
        type: exec.errors?.length ? 'error' : 'success',
      });
      closeImportModal();
      await loadList();
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'Error al importar',
        type: 'error',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const id = recipeToDelete?.id;
    if (!id) return;
    try {
      const res = await recetaPlatoService.remove(id);
      if (res.success) {
        setToast({ isOpen: true, message: 'Receta eliminada', type: 'success' });
        await loadList();
      } else {
        setToast({
          isOpen: true,
          message: res.message || 'No se pudo eliminar',
          type: 'error',
        });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al eliminar la receta',
        type: 'error',
      });
    }
  };

  if (loadingClasificacion) {
    return (
      <div className="p-6 max-w-5xl mx-auto flex justify-center items-center min-h-[16rem]">
        <p className="text-gray-600">Cargando clasificación de recetas…</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to={moduleBase} className="hover:text-teal-600">
          {moduleName}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Recetas</span>
      </nav>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Recetas</h1>
          <p className="text-gray-600 text-sm mt-1">
            <strong>Nombre</strong> de la receta y <strong>clasificación</strong> (categoría, subcategoría y familia).
            Los ingredientes solo pueden ser <strong>productos insumo</strong> (marcados en el alta de producto).
            {includeOutputConfig ? (
              <>
                {' '}
                Para cambiar en masa el <strong>uso en guía PT</strong> de recetas PEP: exporte a Excel, edite esa columna
                e importe el mismo archivo.
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={exporting || loadingList}
            className="px-4 py-2 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {exporting ? 'Generando…' : 'Exportar Excel'}
          </button>
          <button
            type="button"
            onClick={() => downloadRecetasImportTemplate({ includeOutputConfig })}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 text-sm font-medium transition-colors"
          >
            Descargar plantilla
          </button>
          <button
            type="button"
            onClick={onPickRecetasImportFile}
            disabled={importing}
            className="px-4 py-2 rounded-md border border-teal-200 bg-teal-50 hover:bg-teal-100 text-teal-900 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {importing ? 'Leyendo…' : 'Importar Excel'}
          </button>
          <input
            ref={recetasImportInputRef}
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={onRecetasImportFileChange}
          />
          <Link
            to={`${listRecetasBase}/nueva`}
            className="px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium transition-colors inline-flex items-center"
          >
            Nueva receta
          </Link>
        </div>
      </div>

      {listError && (
        <p className="mb-4 text-sm text-red-600 border border-red-100 bg-red-50 rounded-md px-3 py-2">{listError}</p>
      )}

      <div className="mb-4 p-4 bg-white rounded-lg shadow border border-gray-200">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px] flex-1 sm:max-w-xs">
            <label htmlFor="receta-filtro-nombre" className="block text-xs font-medium text-gray-600 mb-1">
              Buscar por nombre
            </label>
            <input
              id="receta-filtro-nombre"
              type="search"
              value={nombreBusqueda}
              onChange={(e) => setNombreBusqueda(e.target.value)}
              placeholder="Ej. concha, pastel…"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <div className="min-w-[160px]">
            <label htmlFor="receta-filtro-categoria" className="block text-xs font-medium text-gray-600 mb-1">
              Categoría
            </label>
            <select
              id="receta-filtro-categoria"
              value={categoriaId}
              onChange={onCategoriaChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">Todas</option>
              {hierarchy.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[160px]">
            <label htmlFor="receta-filtro-sub" className="block text-xs font-medium text-gray-600 mb-1">
              Subcategoría
            </label>
            <select
              id="receta-filtro-sub"
              value={subcategoriaId}
              onChange={onSubcategoriaChange}
              disabled={!categoriaId}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option value="">Todas</option>
              {subcategoriasFiltradas.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[160px]">
            <label htmlFor="receta-filtro-familia" className="block text-xs font-medium text-gray-600 mb-1">
              Familia
            </label>
            <select
              id="receta-filtro-familia"
              value={familiaId}
              onChange={(e) => setFamiliaId(e.target.value)}
              disabled={!subcategoriaId}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option value="">Todas</option>
              {familiasFiltradas.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          {showLineaFilter && (
            <div className="min-w-[160px]">
              <label htmlFor="receta-filtro-linea" className="block text-xs font-medium text-gray-600 mb-1">
                Línea de producción
              </label>
              <select
                id="receta-filtro-linea"
                value={lineaFiltro}
                onChange={(e) => setLineaFiltro(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">Todas</option>
                <option value="none">Sin línea</option>
                {lineas.map((linea) => (
                  <option key={linea.id} value={linea.id}>
                    {linea.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="min-w-[130px]">
            <label htmlFor="receta-filtro-estado" className="block text-xs font-medium text-gray-600 mb-1">
              Estado
            </label>
            <select
              id="receta-filtro-estado"
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value={ESTADO_FILTRO.TODOS}>Todos</option>
              <option value={ESTADO_FILTRO.ACTIVO}>Activo</option>
              <option value={ESTADO_FILTRO.INACTIVO}>Inactivo</option>
            </select>
          </div>
          {hayFiltrosActivos && (
            <button
              type="button"
              onClick={limpiarFiltros}
              className="text-sm text-gray-600 hover:text-teal-700 underline whitespace-nowrap pb-2"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
        {loadingList ? (
          <div className="p-8 text-center text-gray-600 text-sm">Cargando recetas…</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-4 py-3 font-medium w-[32%] sm:w-[28%]">Nombre</th>
                <th className="text-left px-4 py-3 font-medium">Clasificación</th>
                {includeOutputConfig ? (
                  <th className="text-left px-4 py-3 font-medium whitespace-nowrap min-w-[9.5rem]">
                    Tipo de salida
                  </th>
                ) : null}
                {includeOutputConfig ? (
                  <th className="text-left px-4 py-3 font-medium whitespace-nowrap min-w-[9rem]">
                    Uso en guía PT
                  </th>
                ) : null}
                {showLineaFilter ? (
                  <th className="text-left px-4 py-3 font-medium whitespace-nowrap">Línea</th>
                ) : null}
                <th className="text-left px-4 py-3 font-medium whitespace-nowrap w-28">Estado</th>
                <th className="text-right px-4 py-3 font-medium whitespace-nowrap min-w-[8rem]"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={includeOutputConfig ? (showLineaFilter ? 7 : 6) : (showLineaFilter ? 5 : 4)} className="px-4 py-8 text-center text-gray-500 text-sm">
                    No hay recetas que coincidan con los filtros.{' '}
                    <button
                      type="button"
                      onClick={limpiarFiltros}
                      className="text-teal-700 font-medium hover:underline"
                    >
                      Limpiar filtros
                    </button>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900 align-top">{row.name}</td>
                    <td className="px-4 py-3 text-gray-600 align-top leading-snug">{row.classification}</td>
                    {includeOutputConfig ? (
                      <td className="px-4 py-3 align-top whitespace-nowrap">{outputTypeBadge(row)}</td>
                    ) : null}
                    {includeOutputConfig ? (
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        {pepUsoEnPtBadge(row, produccionFabricarPepDesdePtEnabled)}
                      </td>
                    ) : null}
                    {showLineaFilter ? (
                      <td className="px-4 py-3 align-top text-gray-600 whitespace-nowrap">
                        {row.produccion_linea_name || '—'}
                      </td>
                    ) : null}
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {row.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="inline-flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                        <button
                          type="button"
                          onClick={() => navigate(`${listRecetasBase}/editar/${row.id}`)}
                          className="text-sm text-teal-700 hover:text-teal-900 font-medium"
                        >
                          Editar
                        </button>
                        {row.can_delete ? (
                          <button
                            type="button"
                            onClick={() => openDeleteModal(row)}
                            className="text-sm text-red-600 hover:text-red-800 font-medium"
                          >
                            Borrar
                          </button>
                        ) : (
                          <span
                            className="text-xs text-gray-400 cursor-default"
                            title="Tiene movimientos registrados; no se puede eliminar aquí"
                          >
                            —
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-xs text-gray-600">
        {loadingList ? '…' : `Mostrando ${rows.length} receta(s).`}
        {hayFiltrosActivos ? <span className="text-teal-700"> Filtros aplicados.</span> : null}
      </p>

      <div className="mt-6 flex flex-wrap gap-4">
        <Link
          to={`${moduleBase}/clasificacion-recetas/categorias`}
          className="text-teal-700 text-sm font-medium hover:underline"
        >
          ← Clasificación de recetas (Categorías)
        </Link>
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setRecipeToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar receta"
        message={
          recipeToDelete
            ? `¿Eliminar la receta «${recipeToDelete.name}»? Esta acción no se puede deshacer.`
            : ''
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />

      <RecetasImportPreviewModal
        isOpen={importModalOpen}
        onClose={closeImportModal}
        preview={importPreview}
        includeOutputConfig={includeOutputConfig}
        onConfirm={confirmRecetasImport}
        confirming={importing}
      />
    </div>
  );
};

export default RecetasListado;
